import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, getDocs, orderBy, getDoc, doc, where } from 'firebase/firestore';
import GrowthAreaChart from './GrowthAreaChart';
import { ChevronDownIcon, ChevronUpIcon, UserCircleIcon, BookOpenIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/authContext';

const HistoryCard = ({ item }) => {
  const [showInsights, setShowInsights] = useState(false);

  // Determine item type and styling
  let icon, badgeColor, badgeText, title;
  if (item.type === 'prayer') {
    icon = <span className="text-2xl">🙏</span>;
    badgeColor = 'bg-purple-50 text-purple-700';
    badgeText = 'Prayer';
    title = 'Prayer';
  } else if (item.type === 'content_completion') {
    icon = <span className="text-2xl">✓</span>;
    badgeColor = 'bg-green-50 text-green-800';
    badgeText = 'Completed';
    title = 'Resource completed';
  } else if (item.type === 'content') {
    icon = <BookOpenIcon className="h-5 w-5" />;
    badgeColor = item.messageType === 'email' ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700';
    badgeText = item.messageType === 'email' ? 'Content (Email)' : 'Content (SMS)';
    title = item.resourceTitle || 'Content';
  } else {
    icon = <UserCircleIcon className="h-5 w-5" />;
    badgeColor = item.type === 'email' 
      ? 'bg-blue-50 text-blue-700'
      : 'bg-green-50 text-green-700';
    badgeText = item.type === 'email' ? 'Email' : 'SMS';
    title = item.verseReference || 'Message';
  }

  return (
    <div className="border p-4 rounded-lg shadow-sm mb-4 bg-white">
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="text-lg font-semibold text-gray-900 break-words">
            {title}
          </h3>
        </div>
        <span className={`px-2 py-1 rounded-full text-sm font-medium ${badgeColor}`}>
          {badgeText}
        </span>
      </div>

      <div className="space-y-3">
        {/* Prayer Content */}
        {item.type === 'prayer' && item.prayerText && (
          <div className="bg-purple-50 p-3 rounded-md break-words whitespace-pre-line italic">
            <span className="text-gray-900">{item.prayerText}</span>
          </div>
        )}

        {/* Completion notice (disciple tapped Finished on article link) */}
        {item.type === 'content_completion' && item.text && (
          <div className="bg-green-50 border border-green-100 p-3 rounded-md break-words">
            <span className="text-gray-900 font-medium">{item.text}</span>
          </div>
        )}

        {/* Content Message */}
        {item.type === 'content' && (
          <>
            {item.resourceTitle && (
              <div className="bg-green-50 p-3 rounded-md flex items-center gap-2">
                <BookOpenIcon className="h-5 w-5 text-green-600 flex-shrink-0" />
                <span className="font-medium text-green-700">{item.resourceTitle}</span>
              </div>
            )}
            {item.text && (
              <div className="bg-gray-50 p-3 rounded-md break-words whitespace-pre-line">
                <span className="text-gray-900">{item.text}</span>
              </div>
            )}
          </>
        )}

        {/* Regular Message Content */}
        {item.type === 'message' && (
          <>
            {item.customMessage && (
              <div className="bg-gray-50 p-3 rounded-md break-words whitespace-pre-line">
                <span className="text-gray-900 break-words whitespace-pre-line">{item.customMessage}</span>
              </div>
            )}

            {item.question && (
              <div className="bg-blue-50 p-3 rounded-md break-words whitespace-pre-line">
                <span className="font-medium text-blue-700">Reflection Question: </span>
                <span className="text-gray-900 break-words whitespace-pre-line">{item.question}</span>
              </div>
            )}

            {item.verseReference && (
              <button
                onClick={() => setShowInsights(!showInsights)}
                className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mt-2"
              >
                {showInsights ? (
                  <ChevronUpIcon className="h-4 w-4" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4" />
                )}
                Insights
              </button>
            )}

            {showInsights && item.verseReference && (
              <div className="space-y-3 pt-2">
                <div className="bg-yellow-50 p-3 rounded-md">
                  <span className="font-medium text-yellow-700">Verse: </span>
                  <span className="text-gray-900 italic">{item.text}</span>
                </div>

                {item.growthArea && (
                  <span className={`px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-700 border border-purple-200`}>
                    {item.growthArea}
                  </span>
                )}

                {item.devTopic && (
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">
                      {item.devTopic}
                    </span>
                  </div>
                )}

                {item.concept && (
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700 border border-blue-100">
                      {item.concept}
                    </span>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <div className="mt-4 text-sm text-gray-500 flex justify-between items-center">
        <span>
          {item.type === 'prayer' ? 'Prayed' : item.type === 'content' ? 'Sent' : item.type === 'content_completion' ? 'Recorded' : 'Sent'} on{' '}
          {new Date((item.sentAt || item.createdAt)?.seconds * 1000 || (item.sentAt || item.createdAt)).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          })}
        </span>
        {item.url && (
          <a 
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            View on Bible.com
          </a>
        )}
      </div>
    </div>
  );
};

const DiscipleHistoryContent = ({ onClose }) => {
  const { discipleId } = useParams();
  const [messages, setMessages] = useState([]);
  const [messageType, setMessageType] = useState('all');
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [startDate, setStartDate] = useState(firstOfMonth.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);
  const [datePreset, setDatePreset] = useState('this-month');
  const [isLoading, setIsLoading] = useState(true);
  const { currentUser } = useAuth();
  const [discipleData, setDiscipleData] = useState(null);
  const [mentorDetails, setMentorDetails] = useState(null);
  const [userAppID, setUserAppID] = useState('');
  const [showAnalysis, setShowAnalysis] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser || !discipleId) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        const appID = userDoc.exists() ? userDoc.data().appID : null;

        if (!appID) {
          throw new Error("User appID not found.");
        }
        setUserAppID(appID);

        const [discipleDoc, mentorDetailsDoc] = await Promise.all([
          getDoc(doc(db, 'disciples', discipleId)),
          getDoc(doc(db, 'disciples', discipleId, 'mentorDetails', appID))
        ]);

        if (discipleDoc.exists()) {
          setDiscipleData(discipleDoc.data());
        } else {
          console.warn("Disciple document not found.");
        }
        if (mentorDetailsDoc.exists()) {
          setMentorDetails(mentorDetailsDoc.data());
        } else {
          console.warn("Mentor details document not found.");
        }

        const versesSnapshot = await getDocs(collection(db, 'verses'));
        const versesMap = {};
        versesSnapshot.docs.forEach(doc => {
          const verseData = doc.data();
          versesMap[verseData.reference] = {
            growthArea: verseData.growthArea || '',
            devTopic: verseData.devTopic || '',
            concept: verseData.concept || '',
            url: verseData.url || '',
            tags: verseData.tags || [],
            categories: verseData.categories || []
          };
        });

        let allItems = [];
        
        // Load regular messages (email)
        const emailCollectionRef = collection(db, 'disciples', discipleId, 'mentorDetails', appID, 'messages');
        const emailSnapshot = await getDocs(query(emailCollectionRef, orderBy('sentAt', 'desc')));
        const emailMessages = emailSnapshot.docs.map(doc => {
            const data = doc.data();
            const verseData = versesMap[data.verseReference] || {};
            // Check if it's a content message
            if (data.resourceId) {
              return { 
                ...data, 
                ...verseData, 
                id: doc.id, 
                type: 'content',
                messageType: 'email',
                source: 'messages'
              };
            }
            return { ...data, ...verseData, id: doc.id, type: 'message', messageType: 'email', source: 'messages' };
        }).filter(msg => !msg.placeholder && (msg.verseReference || msg.type === 'content'));
        allItems = [...allItems, ...emailMessages];

        // Load SMS messages
        const smsCollectionRef = collection(db, 'disciples', discipleId, 'mentorDetails', appID, 'messagesSMS');
        const smsSnapshot = await getDocs(smsCollectionRef);
        const smsMessages = smsSnapshot.docs.map(doc => {
            const data = doc.data();
            const verseData = versesMap[data.verseReference] || {};
            // Check if it's a content message
            if (data.resourceId) {
              return { 
                ...data, 
                ...verseData, 
                id: doc.id, 
                type: 'content',
                messageType: 'sms',
                sentAt: data.sentAt || data.createdAt,
                source: 'messages'
              };
            }
            return { ...data, ...verseData, id: doc.id, type: 'message', messageType: 'sms', sentAt: data.sentAt || data.createdAt, source: 'messages' };
        }).filter(msg => !msg.placeholder && (msg.verseReference || msg.type === 'content'));
        allItems = [...allItems, ...smsMessages];

        // Load history subcollection (prayers and content messages)
        try {
          const historyCollectionRef = collection(db, 'disciples', discipleId, 'mentorDetails', appID, 'history');
          const historySnapshot = await getDocs(historyCollectionRef);
          
          historySnapshot.docs.forEach(doc => {
            const data = doc.data();
            if (data.type === 'prayer') {
              allItems.push({
                id: doc.id,
                type: 'prayer',
                prayerText: data.prayerText,
                createdAt: data.createdAt,
                source: 'history'
              });
            } else if (data.type === 'content') {
              allItems.push({
                id: doc.id,
                type: 'content',
                text: data.text,
                resourceId: data.resourceId,
                resourceTitle: data.resourceTitle,
                messageType: data.messageType,
                createdAt: data.createdAt,
                source: 'history'
              });
            }
          });
        } catch (error) {
          console.error('Error loading history:', error);
        }

        // Completion notices (stored on mentorDetails when disciple marks article Finished)
        const completionNotices = mentorDetailsDoc.exists()
          ? mentorDetailsDoc.data()?.completionNotices || []
          : [];
        completionNotices.forEach((n, idx) => {
          const createdAt = n.createdAt;
          allItems.push({
            id: `completionNotice-${idx}-${createdAt?.seconds ?? createdAt ?? idx}`,
            type: 'content_completion',
            text: n.text,
            resourceId: n.resourceId,
            resourceTitle: n.resourceTitle,
            createdAt,
            sentAt: createdAt,
            source: 'mentorDetails',
          });
        });

        const getTimestampMillis = (item) => {
          const timestamp = item.sentAt || item.createdAt;
          if (!timestamp) return 0;
          return timestamp.seconds ? timestamp.seconds * 1000 : new Date(timestamp).getTime();
        };

        const dedupeKeyForItem = (item) => {
          const timestampMillis = getTimestampMillis(item);
          if (item.type === 'content') {
            return `content-${item.messageType || 'unknown'}-${item.resourceId || 'no-resource'}-${timestampMillis}`;
          }
          if (item.type === 'content_completion') {
            return `content_completion-${item.resourceId || 'na'}-${timestampMillis}`;
          }
          if (item.type === 'message') {
            return `message-${item.messageType || 'unknown'}-${item.verseReference || 'no-verse'}-${timestampMillis}`;
          }
          if (item.type === 'prayer') {
            return `prayer-${timestampMillis}-${(item.prayerText || '').slice(0, 32)}`;
          }
          return `${item.type || 'unknown'}-${timestampMillis}-${item.id}`;
        };

        const dedupedItemsMap = new Map();
        allItems.forEach(item => {
          const key = dedupeKeyForItem(item);
          if (!dedupedItemsMap.has(key)) {
            dedupedItemsMap.set(key, item);
            return;
          }
          const existing = dedupedItemsMap.get(key);
          if (existing?.source === 'history' && item?.source === 'messages') {
            dedupedItemsMap.set(key, item);
          }
        });
        allItems = Array.from(dedupedItemsMap.values());

        if (startDate && endDate) {
          const start = new Date(startDate + 'T00:00:00');
          const end = new Date(endDate + 'T23:59:59');

          allItems = allItems.filter(item => {
            const itemTime = getTimestampMillis(item);
            if (!itemTime) return false;
            const itemDate = new Date(itemTime);
            return itemDate >= start && itemDate <= end;
          });
        }

        // Filter by message type if specified
        if (messageType !== 'all') {
          allItems = allItems.filter(item => {
            if (item.type === 'prayer') return false;
            if (item.type === 'content_completion') return false;
            if (item.type === 'content') {
              return item.messageType === messageType;
            }
            return item.messageType === messageType || item.type === messageType;
          });
        }

        const sorted = [...allItems].sort((a, b) => getTimestampMillis(b) - getTimestampMillis(a));
        setMessages(sorted);

      } catch (error) {
        console.error('Error fetching initial data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [discipleId, currentUser, startDate, endDate, messageType]);

  useEffect(() => {
    const normalizeDate = (date) => date.toISOString().split('T')[0];
    const end = new Date();
    if (datePreset === 'this-month') {
      setStartDate(new Date(end.getFullYear(), end.getMonth(), 1).toISOString().split('T')[0]);
      setEndDate(normalizeDate(end));
      return;
    }
    if (datePreset === 'last-7') {
      const start = new Date(end);
      start.setDate(end.getDate() - 6);
      setStartDate(normalizeDate(start));
      setEndDate(normalizeDate(end));
      return;
    }
    if (datePreset === 'last-30') {
      const start = new Date(end);
      start.setDate(end.getDate() - 29);
      setStartDate(normalizeDate(start));
      setEndDate(normalizeDate(end));
    }
  }, [datePreset]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-4 sm:p-6">
        <div className="mb-4 flex flex-col gap-3 sm:gap-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
              <span>History Feed</span>
              <span className="text-gray-400">•</span>
              <span className="text-gray-500">{messages.length} items</span>
            </div>
            <button
              onClick={() => setShowAnalysis(prev => !prev)}
              className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            >
              {showAnalysis ? (
                <>
                  <ChevronUpIcon className="h-4 w-4" />
                  Hide Analysis
                </>
              ) : (
                <>
                  <ChevronDownIcon className="h-4 w-4" />
                  View Analysis
                </>
              )}
            </button>
          </div>
          {showAnalysis && (
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-800">Growth Analysis</h3>
                <span className="text-xs text-gray-500">Chart summary</span>
              </div>
              <GrowthAreaChart messages={messages} />
            </div>
          )}
        </div>

        <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm sticky top-0 z-10">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1.2fr_1fr]">
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Message Type</label>
              <div className="flex items-center gap-2">
                <select
                  value={messageType}
                  onChange={(e) => setMessageType(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="all">All History</option>
                  <option value="email">Email Only</option>
                  <option value="sms">SMS Only</option>
                </select>
                <button
                  onClick={() => {
                    setDatePreset('this-month');
                  }}
                  className="whitespace-nowrap rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
                >
                  Reset
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Date Range</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'this-month', label: 'This Month' },
                  { value: 'last-7', label: 'Last 7' },
                  { value: 'last-30', label: 'Last 30' },
                  { value: 'custom', label: 'Custom' }
                ].map(option => (
                  <button
                    key={option.value}
                    onClick={() => setDatePreset(option.value)}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                      datePreset === option.value
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              {datePreset === 'custom' && (
                <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                  <input
                    type="date"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    className="w-full bg-transparent text-sm text-gray-700 focus:outline-none"
                  />
                  <span className="text-xs font-medium text-gray-400">to</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                    className="w-full bg-transparent text-sm text-gray-700 focus:outline-none"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="space-y-4">
          {isLoading ? (
            <p className="text-sm text-gray-500">Loading...</p>
          ) : messages.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-200 bg-white p-6 text-center text-sm text-gray-500">
              No history yet for this time range.
            </div>
          ) : (
            messages.map((item) => (
              <HistoryCard key={item.id} item={item} />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default DiscipleHistoryContent; 